/**
 * ConfirmDialog + useConfirm — the in-app styled replacement for the native
 * `window.confirm` (E04-F02-T09).
 *
 * PM requirement: "все уведомления в системе должны быть в дизайне системы" —
 * every yes/no confirmation must go through the design system, not the browser's
 * unstyled `confirm()` chrome. This composes the shared {@link Dialog} primitive
 * (navy header + white body + bordered footer) with {@link DialogFooter}, whose
 * `danger` variant (red fill) is used for destructive confirmations (e.g. Unlink).
 *
 * Two exports:
 *   - {@link ConfirmDialog} — the presentational card (open + message + two
 *     actions). Use it directly when you already own the open/result state.
 *   - {@link useConfirm} — a promise-based hook that mirrors `window.confirm`'s
 *     call shape so existing call sites barely change:
 *
 *       const { confirm, confirmDialog } = useConfirm();
 *       // in JSX: {confirmDialog}
 *       async function onClick() {
 *         if (!(await confirm({ message: 'Delete this?' }))) return;
 *         // …proceed
 *       }
 *
 *     The hook is self-contained (renders its own dialog node, no global
 *     provider): each consumer drops `{confirmDialog}` into its tree and gets a
 *     `confirm()` that resolves `true`/`false` when the user picks an action
 *     (Esc / scrim / Cancel → false, matching native `confirm` = cancel).
 */
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { Dialog, DialogFooter, type DialogFooterProps } from './Dialog.js';

/** Options describing a single confirmation prompt. */
export interface ConfirmOptions {
  /** Heading in the navy title bar. Defaults to "Please confirm". */
  readonly title?: ReactNode;
  /** The question / body text. */
  readonly message: ReactNode;
  /** Confirm-button label. Defaults to "Confirm". */
  readonly confirmLabel?: string;
  /** Cancel-button label. Defaults to "Cancel". */
  readonly cancelLabel?: string;
  /**
   * Visual weight of the confirm action — reuses the {@link DialogFooter}
   * variants. Use `danger` (red) for destructive confirmations. Defaults to
   * `primary`.
   */
  readonly variant?: DialogFooterProps['variant'];
  /** Test id prefix (buttons get `<testId>-confirm` / `<testId>-cancel`). */
  readonly testId?: string;
}

export interface ConfirmDialogProps extends ConfirmOptions {
  /** Whether the confirmation is shown. */
  readonly open: boolean;
  /** User confirmed (the primary action). */
  readonly onConfirm: () => void;
  /** User cancelled (Cancel button, Esc, or scrim click). */
  readonly onCancel: () => void;
}

/**
 * Presentational confirm card — a small {@link Dialog} with a message body and a
 * Cancel + Confirm footer. The parent owns `open` and the two handlers.
 */
export function ConfirmDialog({
  open,
  title = 'Please confirm',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'primary',
  testId = 'confirm-dialog',
  onConfirm,
  onCancel,
}: ConfirmDialogProps): React.JSX.Element | null {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      testId={testId}
      maxWidth="sm"
      footer={
        <DialogFooter
          onCancel={onCancel}
          onConfirm={onConfirm}
          cancelLabel={cancelLabel}
          confirmLabel={confirmLabel}
          variant={variant}
          testId={testId}
        />
      }
    >
      <p className="text-sm text-text" data-testid={`${testId}-message`}>
        {message}
      </p>
    </Dialog>
  );
}

interface ConfirmState {
  readonly open: boolean;
  readonly options: ConfirmOptions;
}

const CLOSED: ConfirmState = { open: false, options: { message: '' } };

export interface UseConfirmResult {
  /**
   * Open a styled confirmation and resolve when the user answers: `true` on
   * confirm, `false` on cancel / Esc / scrim (parity with `window.confirm`).
   */
  readonly confirm: (options: ConfirmOptions) => Promise<boolean>;
  /** Render this node somewhere in the component tree so the dialog can mount. */
  readonly confirmDialog: React.JSX.Element;
}

/**
 * Promise-based confirm, self-contained (no provider needed). Returns a
 * `confirm(options)` that resolves to a boolean and a `confirmDialog` node to
 * render in the consumer's tree.
 */
export function useConfirm(): UseConfirmResult {
  const [state, setState] = useState<ConfirmState>(CLOSED);
  const resolverRef = useRef<((result: boolean) => void) | null>(null);

  const settle = useCallback((result: boolean): void => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState((prev) => ({ ...prev, open: false }));
    resolve?.(result);
  }, []);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    // Resolve any still-pending prompt as cancelled before opening a new one.
    resolverRef.current?.(false);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setState({ open: true, options });
    });
  }, []);

  const confirmDialog = (
    <ConfirmDialog
      {...state.options}
      open={state.open}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  );

  return { confirm, confirmDialog };
}

export default ConfirmDialog;
